import { financialItemPath } from 'src/financial/financial-data/financial-item/financialItemPaths';
// The asset's product and description are withheld with the client's name when the viewer can't
// open the client.
export type ProviderReferenceHolder = {
  asset_id: string;
  asset_product: string | null;
  asset_description: string | null;
  client_id: string;
  client_full_name: string | null;
  adviser_full_name: string | null;
  is_joint: boolean;
};

type HolderDescription = {
  label: string;
  path: string | null;
};

// The backend leaves out the client's name when the viewer can't open the client, and the adviser's
// when it is the viewed client's own.
export type ClientAndAdviserNames = {
  client_full_name: string | null;
  adviser_full_name: string | null;
};

/** For use mid-sentence: an unnamed client reads "a client of <adviser>". */
export const describeClientByName = ({
  client_full_name,
  adviser_full_name,
}: ClientAndAdviserNames) => {
  if (client_full_name) {
    return adviser_full_name
      ? `${client_full_name} (adviser ${adviser_full_name})`
      : client_full_name;
  }
  return adviser_full_name ? `a client of ${adviser_full_name}` : 'another client';
};

export const describeProviderReferenceHolders = (holders: ProviderReferenceHolder[]) =>
  new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' }).format(
    holders.map(describeClientByName),
  );

// Descriptions are shown as entered: feed valuations set some, and they carry provider and
// product names whose casing matters.
export const describeHolder = (holder: ProviderReferenceHolder): HolderDescription => {
  if (holder.client_full_name) {
    return {
      label: `${holder.client_full_name}'s ${holder.asset_description ?? 'asset'}`,
      path: holder.asset_product
        ? financialItemPath(holder.client_id, holder.asset_product, holder.asset_id)
        : null,
    };
  }
  return {
    label: holder.adviser_full_name
      ? `an asset for a client of ${holder.adviser_full_name}`
      : 'an asset for another client',
    path: null,
  };
};

export const describeSharedReferenceResolution = ({
  cannotBeJoint,
  ownersEnabled,
  feedMatchingRules,
}: {
  cannotBeJoint: boolean;
  ownersEnabled: boolean;
  feedMatchingRules: boolean;
}) => {
  // Without the feed matching rules, a shared reference stops automatic valuations.
  if (cannotBeJoint) {
    return feedMatchingRules
      ? "A pension or ISA can't be held jointly, so one of the references is probably wrong."
      : "A pension can't be held jointly, so one of the references is probably wrong. Automatic valuations are paused until it's corrected.";
  }
  // With owners on, a joint holding is one item with every owner on it, not a copy each.
  const jointResolution = ownersEnabled
    ? 'record it once: add the other client as an owner of this asset and delete their copy when asked'
    : 'answer yes to "Is this joint?" on each client\'s copy';
  const paused = feedMatchingRules ? '' : ' Automatic valuations are paused until then.';
  return `If this is a joint holding, ${jointResolution}; otherwise one of the references is probably wrong.${paused}`;
};
